import { useAchievementsContext } from '@/contexts/AchievementsContext';
import { useTranslation } from '@/hooks/useTranslation';
import { RARITY_CONFIG, type AchievementWithStatus } from '@/lib/achievements';
import { Trophy, Lock, Check, TrendingUp } from 'lucide-react';
import { useEffect } from 'react';

function AchievementCard({
  achievement,
  language,
}: {
  achievement: AchievementWithStatus;
  language: string;
}) {
  const rarity = RARITY_CONFIG[achievement.rarity];
  const isUnlocked = achievement.status === 'unlocked' || achievement.status === 'newly_unlocked';
  const isNew = achievement.status === 'newly_unlocked';
  const isInProgress = achievement.status === 'in_progress';

  return (
    <div
      className={`relative flex items-start gap-3.5 p-4 rounded-2xl border transition-all ${
        isNew
          ? 'bg-[#16c47f]/5 dark:bg-[#16c47f]/10 border-[#16c47f]/20 dark:border-[#16c47f]/30'
          : isUnlocked
            ? 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/50'
            : 'bg-gray-50/50 dark:bg-gray-800/50 border-gray-100/60 dark:border-gray-700/30'
      }`}
    >
      {/* Icon */}
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${
          isUnlocked ? rarity.bgColor : 'bg-gray-100 dark:bg-gray-700/50'
        }`}
      >
        {isUnlocked ? (
          achievement.icon
        ) : (
          <Lock className="w-4.5 h-4.5 text-gray-400 dark:text-gray-500" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4
              className={`text-sm font-semibold ${
                isUnlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {language === 'fr' ? achievement.nameFr : achievement.nameEn}
            </h4>
            <p
              className={`text-xs mt-0.5 ${
                isUnlocked ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {language === 'fr' ? achievement.descriptionFr : achievement.descriptionEn}
            </p>
          </div>

          {/* Status badge */}
          <div className="flex-shrink-0 mt-0.5">
            {isNew && (
              <span className="text-[10px] font-bold text-[#16c47f] bg-[#16c47f]/10 px-2 py-0.5 rounded-full">
                {language === 'fr' ? 'Nouveau' : 'New'}
              </span>
            )}
            {isUnlocked && !isNew && (
              <div className="w-5 h-5 rounded-full bg-[#16c47f]/10 flex items-center justify-center">
                <Check className="w-3 h-3 text-[#16c47f]" />
              </div>
            )}
          </div>
        </div>

        {/* Progress bar (for in_progress or count-based achievements) */}
        {(isInProgress || (isUnlocked && achievement.targetCount)) &&
          achievement.progress !== undefined && (
            <div className="mt-2.5">
              <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    isUnlocked ? 'bg-[#16c47f]' : 'bg-gradient-to-r from-blue-400 to-blue-500'
                  }`}
                  style={{ width: `${Math.min(achievement.progress * 100, 100)}%` }}
                />
              </div>
              {!isUnlocked && achievement.targetCount && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                  {Math.round(achievement.progress * achievement.targetCount)}/
                  {achievement.targetCount}
                </p>
              )}
            </div>
          )}

        {/* Unlock date */}
        {isUnlocked && achievement.unlockedAt && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">
            {language === 'fr' ? 'Débloqué le' : 'Unlocked'}{' '}
            {new Date(achievement.unlockedAt).toLocaleDateString(
              language === 'fr' ? 'fr-FR' : 'en-US',
              { day: 'numeric', month: 'long', year: 'numeric' }
            )}
          </p>
        )}
      </div>

      {/* Rarity indicator — hidden when newly_unlocked to avoid overlap with 'New' badge */}
      {isUnlocked && !isNew && achievement.rarity !== 'basic' && (
        <div className="absolute top-2 right-2">
          <span
            className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${rarity.bgColor} ${rarity.color}`}
          >
            {language === 'fr'
              ? RARITY_CONFIG[achievement.rarity].labelFr
              : RARITY_CONFIG[achievement.rarity].labelEn}
          </span>
        </div>
      )}
    </div>
  );
}

export default function AchievementsSettings() {
  const { achievementsByCategory, stats, loading, refresh } = useAchievementsContext();
  const { language } = useTranslation();

  // Refresh achievements data every time this component mounts (tab becomes active)
  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global progress card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#16c47f]/10 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-[#16c47f]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {language === 'fr' ? 'Succès clevia' : 'clevia Achievements'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {language === 'fr'
                ? 'Suivez votre progression et débloquez des succès'
                : 'Track your progress and unlock achievements'}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.unlocked}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {language === 'fr' ? 'Débloqués' : 'Unlocked'}
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {language === 'fr' ? 'Total' : 'Total'}
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
            <p className="text-2xl font-bold text-[#16c47f]">
              {stats.total > 0 ? Math.round((stats.unlocked / stats.total) * 100) : 0}%
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {language === 'fr' ? 'Progression' : 'Progress'}
            </p>
          </div>
        </div>

        {/* Main progress bar */}
        <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#16c47f] to-emerald-400 rounded-full transition-all duration-700"
            style={{ width: `${stats.total > 0 ? (stats.unlocked / stats.total) * 100 : 0}%` }}
          />
        </div>

        {/* Next achievement */}
        {stats.nextAchievement && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100/50 dark:border-blue-800/30">
            <TrendingUp className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <p className="text-xs text-gray-600 dark:text-gray-300">
              <span className="font-semibold">{language === 'fr' ? 'Prochain :' : 'Next:'}</span>{' '}
              {stats.nextAchievement.icon}{' '}
              {language === 'fr' ? stats.nextAchievement.nameFr : stats.nextAchievement.nameEn}
              {stats.nextAchievement.progress !== undefined && (
                <span className="text-blue-500 ml-1">
                  ({Math.round(stats.nextAchievement.progress * 100)}%)
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Categories */}
      {achievementsByCategory.map((group) => {
        const unlockedInCategory = group.achievements.filter(
          (a) => a.status === 'unlocked' || a.status === 'newly_unlocked'
        ).length;

        return (
          <div
            key={group.category}
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6"
          >
            {/* Category header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <span className="text-lg">{group.icon}</span>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {language === 'fr' ? group.labelFr : group.labelEn}
                </h3>
              </div>
              <span className="text-xs font-semibold text-gray-400 dark:text-gray-500">
                {unlockedInCategory}/{group.achievements.length}
              </span>
            </div>

            {/* Achievement cards */}
            <div className="space-y-2.5">
              {group.achievements.map((ach) => (
                <AchievementCard key={ach.key} achievement={ach} language={language} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
